import React from 'react'
import { Alert, AlertDescription, AlertTitle } from './ui/alert'
import { ApiError } from '../lib/api/types'
import { AlertCircle, XCircle, Info } from 'lucide-react'

interface ErrorDisplayProps {
  error: ApiError | null
  onDismiss?: () => void
  className?: string
}

export function ErrorDisplay({ error, onDismiss, className = '' }: ErrorDisplayProps) {
  if (!error) return null

  const getErrorIcon = () => {
    switch (error.httpStatusCode) {
      case 400:
      case 422:
        return <AlertCircle className="h-4 w-4" />
      case 401:
      case 403:
        return <XCircle className="h-4 w-4" />
      default:
        return <Info className="h-4 w-4" />
    }
  }

  const getErrorVariant = () => {
    switch (error.httpStatusCode) {
      case 400:
      case 422:
        return 'destructive' as const
      case 401:
      case 403:
        return 'destructive' as const
      default:
        return 'default' as const
    }
  }

  const getErrorMessage = (error: ApiError): string => {
    // Map specific error codes to user-friendly messages
    switch (error.code) {
      case 'INVALID_REQUEST':
        return 'Please check your input and try again.'
      case 'UNAUTHORIZED_MERCHANT_ACCESS':
        return 'You do not have access to this merchant account. Please check your merchant account ID.'
      case 'MISSING_IDEMPOTENCY_KEY':
        return 'A system error occurred. Please try again.'
      case 'IDEMPOTENCY_CONFLICT':
        return 'This request has already been processed. Please check your payment status.'
      case 'AMOUNT_OUT_OF_RANGE':
        return 'The payment amount is outside the allowed range. Please adjust the amount.'
      case 'INVALID_CURRENCY':
        return 'The selected currency is not supported. Please choose a different currency.'
      case 'NETWORK_ERROR':
        return 'Network error. Please check your internet connection and try again.'
      default:
        return error.message
    }
  }

  const getLocationMessage = (error: ApiError): string | null => {
    if (error.details?.location) {
      const location = error.details.location
      // Convert snake_case to Title Case for display
      const displayLocation = location
        .split('_')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')

      return `Field: ${displayLocation}`
    }
    return null
  }

  return (
    <Alert variant={getErrorVariant()} className={className}>
      {getErrorIcon()}
      <AlertTitle className="flex items-center justify-between">
        <span>Error</span>
        {onDismiss && (
          <button onClick={onDismiss} className="ml-auto text-sm font-medium hover:opacity-70">
            Dismiss
          </button>
        )}
      </AlertTitle>
      <AlertDescription>
        <div className="space-y-2">
          <p>{getErrorMessage(error)}</p>

          {getLocationMessage(error) && (
            <p className="text-sm font-medium text-muted-foreground">{getLocationMessage(error)}</p>
          )}

          {error.details?.allErrors && error.details.allErrors.length > 1 && (
            <div className="mt-2 text-sm">
              <p className="font-medium">Additional errors:</p>
              <ul className="list-disc list-inside space-y-1">
                {error.details.allErrors.slice(1).map((err: any, index: number) => (
                  <li key={index}>
                    <span className="font-mono">{err.code}:</span> {err.message}
                    {err.location && <span className="text-muted-foreground ml-2">({err.location})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error.correlationId && (
            <p className="text-xs text-muted-foreground mt-2">Reference ID: {error.correlationId}</p>
          )}
        </div>
      </AlertDescription>
    </Alert>
  )
}
